import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Download, FileArchive, Calendar, Clock, CheckCircle, XCircle, Loader2, Trash2, AlertCircle, FileText, Users, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPORT_TYPES = {
  ANNUAL: { label: 'Export Annuale', description: 'Tutti i dati dell\'anno selezionato' },
  CUSTOM: { label: 'Export Personalizzato', description: 'Scegli periodo e contenuti' },
  ACCOUNTANT_PACK: { label: 'Pacchetto Contabile', description: 'Spese, IVA e ritenute' }
};

const EXPORT_STATUS = {
  PENDING: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  PROCESSING: { label: 'In elaborazione', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  COMPLETED: { label: 'Completato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  FAILED: { label: 'Errore', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export const Exports = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;

  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    export_type: 'ANNUAL',
    year: new Date().getFullYear(),
    date_from: '',
    date_to: '',
    include_documents: true,
    include_registry: true,
    include_accounting: true,
    include_meetings: true
  });

  const fetchExports = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/exports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setExports(await res.json());
    } catch (error) {
      console.error('Error fetching exports:', error);
    }
    setLoading(false);
  }, [condoId, token]);

  useEffect(() => {
    fetchExports();
    // Poll for status updates
    const interval = setInterval(fetchExports, 10000);
    return () => clearInterval(interval);
  }, [fetchExports]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success('Export avviato');
        setIsCreateOpen(false);
        fetchExports();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore creazione export');
    }
    setCreating(false);
  };

  const handleDownload = async (exportId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/exports/${exportId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export_${exportId.slice(0, 8)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download avviato');
      } else {
        toast.error('Errore download');
      }
    } catch (error) {
      toast.error('Errore download');
    }
  };

  const handleDelete = async (exportId) => {
    if (!window.confirm('Eliminare questo export?')) return;

    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/exports/${exportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Export eliminato');
        fetchExports();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canCreate = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';
  const canDelete = userRole === 'ADMIN';

  if (userRole === 'RESIDENT') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
              <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
              <p className="text-muted-foreground">
                Solo amministratori e contabili possono accedere agli export.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Esportazioni"
        subtitle="Esportazione dati e documenti"
        actions={
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <FileArchive className="mr-2 h-4 w-4" />
              Nuovo Export
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crea Export</DialogTitle>
              <DialogDescription>
                Scegli il tipo di export e i contenuti da includere
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo Export</Label>
                <Select
                  value={form.export_type}
                  onValueChange={(v) => setForm({ ...form, export_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPORT_TYPES).map(([k, v]) => (
                      userRole === 'ACCOUNTANT' && k !== 'ACCOUNTANT_PACK' ? null : (
                        <SelectItem key={k} value={k}>
                          <div>
                            <div className="font-medium">{v.label}</div>
                            <div className="text-xs text-muted-foreground">{v.description}</div>
                          </div>
                        </SelectItem>
                      )
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.export_type === 'ANNUAL' && (
                <div className="space-y-2">
                  <Label>Anno</Label>
                  <Select
                    value={form.year.toString()}
                    onValueChange={(v) => setForm({ ...form, year: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(5)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.export_type === 'CUSTOM' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Inizio</Label>
                    <Input
                      type="date"
                      value={form.date_from}
                      onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Fine</Label>
                    <Input
                      type="date"
                      value={form.date_to}
                      onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {form.export_type !== 'ACCOUNTANT_PACK' && (
                <div className="space-y-3">
                  <Label>Contenuti da includere</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="registry"
                        checked={form.include_registry}
                        onCheckedChange={(c) => setForm({ ...form, include_registry: !!c })}
                      />
                      <Label htmlFor="registry" className="flex items-center gap-1 cursor-pointer">
                        <Users className="h-4 w-4" /> Anagrafica
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="accounting"
                        checked={form.include_accounting}
                        onCheckedChange={(c) => setForm({ ...form, include_accounting: !!c })}
                      />
                      <Label htmlFor="accounting" className="flex items-center gap-1 cursor-pointer">
                        <Receipt className="h-4 w-4" /> Contabilità
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="documents"
                        checked={form.include_documents}
                        onCheckedChange={(c) => setForm({ ...form, include_documents: !!c })}
                      />
                      <Label htmlFor="documents" className="flex items-center gap-1 cursor-pointer">
                        <FileText className="h-4 w-4" /> Documenti
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="meetings"
                        checked={form.include_meetings}
                        onCheckedChange={(c) => setForm({ ...form, include_meetings: !!c })}
                      />
                      <Label htmlFor="meetings" className="flex items-center gap-1 cursor-pointer">
                        <Calendar className="h-4 w-4" /> Assemblee
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creazione...
                    </>
                  ) : (
                    'Crea Export'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        }
      />

      {/* Exports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Storico Export</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : exports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileArchive className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun export creato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Periodo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Dimensione</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exports.map((exp) => {
                  const statusInfo = EXPORT_STATUS[exp.status] || EXPORT_STATUS.PENDING;
                  const StatusIcon = statusInfo.icon;
                  return (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <Badge variant="outline">
                          {EXPORT_TYPES[exp.export_type]?.label || exp.export_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {exp.year || `${exp.date_from} - ${exp.date_to}`}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          <StatusIcon className={`h-3 w-3 mr-1 ${exp.status === 'PROCESSING' ? 'animate-spin' : ''}`} />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatSize(exp.file_size)}</TableCell>
                      <TableCell>{formatDate(exp.created_at)}</TableCell>
                      <TableCell className="text-right space-x-2">
                        {exp.status === 'COMPLETED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(exp.id)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(exp.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};